import { useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import {
  errorMessage,
  GENERIC_ERROR_MESSAGE,
  withAuthOutcome,
} from "@todotogether/shared";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

type AuthMode = "signin" | "signup" | "reset" | "verify";

/** The verify screen's retry copy for a code that didn't take (wrong,
 *  expired, or invalidated by a resend) — one string, both failure paths. */
const VERIFY_CODE_RETRY =
  "That code didn't match or has expired — request a new one below.";

/**
 * Brand marks for the OAuth buttons, used per the sign-in branding
 * guidelines each company publishes for "Continue with ..." buttons.
 * Inline SVG (no external asset): the Google G keeps its four brand
 * colours; the Apple mark follows the button's text colour so it stays
 * correct if the card styling ever changes.
 */
function GoogleMark() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 18 18" className="h-[18px] w-[18px]">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.964 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 384 512" className="h-[18px] w-[18px] fill-current">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

/**
 * Email + password sign-in / sign-up / password-reset / email-verification,
 * plus "continue with" Google and Apple (wired server-side in convex/auth.ts
 * — the buttons route through Convex Auth's OAuth endpoints; they error
 * visibly until the AUTH_GOOGLE_* / AUTH_APPLE_* env vars are configured).
 *
 * Reset is the two-step Password flow: `flow:"reset"` emails a code (via
 * Resend, or the dev sink in local E2E), then `flow:"reset-verification"`
 * with the code + new password completes it and invalidates old sessions.
 *
 * Verify (#126) is the email-verification gate's front door: sign-up and
 * sign-in on an unverified account resolve WITHOUT a session — a code went
 * out instead — so both routes land here. Entering the code
 * (`flow:"email-verification"`) verifies the mailbox and signs in;
 * the resend affordance is the same flow without a code (it invalidates
 * the previous code server-side). Arriving with ?email= means a code was
 * just sent; arriving without it (bookmarks, the legacy pre-gate window)
 * offers a "send me a code" step first. OAuth accounts never see this
 * screen — their provider sign-in verifies them.
 */
export default function AuthPage() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  // An invite link that found you signed out rides along: after auth we
  // return to /invite/:token so the actual join (quota, role, colour)
  // still goes through the server.
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite");
  const afterAuth = () => navigate(invite ? `/invite/${invite}` : "/app");
  // The cross-links below carry the invite along (ticket #160): an
  // invitee detouring between the auth forms must not drop the token,
  // or the post-auth redirect loses the join.
  const inviteQuery = invite ? `?invite=${encodeURIComponent(invite)}` : "";
  // The route IS the mode: deriving it (rather than taking a prop) means
  // the rendered form can never disagree with the URL, however this
  // component gets mounted.
  const { pathname } = useLocation();
  const mode: AuthMode = pathname.startsWith("/signup")
    ? "signup"
    : pathname.startsWith("/reset")
      ? "reset"
      : pathname.startsWith("/verify")
        ? "verify"
        : "signin";
  // Arriving at /verify with ?email= means a code was just sent by the
  // sign-up/sign-in that navigated here; without it, the screen offers a
  // "send me a code" step first.
  const arrivingWithEmail = searchParams.get("email");
  // Arriving at /reset with ?code= is the emailed link (#174): the form
  // opens straight on the second step with whatever the link carried —
  // the fresh mail's link brings code AND address (only the new password
  // is left to type), the legacy root redirect brings just the code.
  // Arriving empty (the manual path) is unchanged: request a code, then
  // type it.
  const arrivingResetCode = mode === "reset" ? searchParams.get("code") : null;
  // The verify screen's own ?code= (#192): the emailed verification link
  // brings code AND address, so the screen opens on the code-entry step
  // with the code prefilled — one explicit Verify click left. The code
  // is only meaningful with its address (the flow is keyed to it), so a
  // lone ?code= is ignored and the manual path is unchanged.
  const arrivingVerifyCode =
    mode === "verify" && arrivingWithEmail !== null ? searchParams.get("code") : null;
  const [email, setEmail] = useState(arrivingWithEmail ?? "");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState(arrivingResetCode ?? arrivingVerifyCode ?? "");
  const [resetSent, setResetSent] = useState(
    mode === "reset" && arrivingResetCode !== null,
  );
  const [codeSent, setCodeSent] = useState(mode === "verify" && arrivingWithEmail !== null);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Every auth call on this page rides the bounded outcome (#334): a
  // backend the client can't reach never settles the promise, and the
  // `finally` that clears `busy` would never run — the spinner would
  // spin forever with no error copy. Real failures keep their own copy.
  const boundedSignIn = (...args: Parameters<typeof signIn>) =>
    withAuthOutcome(() => signIn(...args));

  async function submitPasswordFlow(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "reset" && !resetSent) {
        await boundedSignIn("password", { email, flow: "reset" });
        setResetSent(true);
        return;
      }
      if (mode === "reset" && resetSent) {
        await boundedSignIn("password", {
          email,
          flow: "reset-verification",
          code,
          newPassword,
        });
        afterAuth();
        return;
      }
      if (mode === "verify" && !codeSent) {
        await boundedSignIn("password", { email, flow: "email-verification" });
        setCodeSent(true);
        return;
      }
      if (mode === "verify") {
        try {
          const result = await boundedSignIn("password", {
            email,
            flow: "email-verification",
            code,
          });
          if (result.signingIn) {
            afterAuth();
          } else {
            setError(VERIFY_CODE_RETRY);
          }
        } catch {
          // Wrong/expired code (a resend invalidates the previous one) —
          // the library's "Could not verify code" is accurate but terse.
          setError(VERIFY_CODE_RETRY);
        }
        return;
      }
      const result = await boundedSignIn("password", {
        email,
        password,
        flow: mode === "signup" ? "signUp" : "signIn",
      });
      if (result.signingIn) {
        afterAuth();
      } else {
        // Verification is on (#126): the account is unverified, so the
        // call resolved WITHOUT a session — a fresh code went out.
        // Continue on the verify screen; the invite rides along (#160).
        navigate(
          `/verify?email=${encodeURIComponent(email)}${invite ? `&invite=${encodeURIComponent(invite)}` : ""}`,
        );
      }
    } catch (err) {
      setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    } finally {
      setBusy(false);
    }
  }

  /** The verify screen's resend: same flow, no code — mints a fresh one
   *  (and kills the previous, server-side). */
  async function resendCode() {
    setError(null);
    setBusy(true);
    try {
      await boundedSignIn("password", { email, flow: "email-verification" });
      setResent(true);
      setCode("");
    } catch (err) {
      setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    } finally {
      setBusy(false);
    }
  }

  async function oauth(provider: "google" | "apple" | "mock") {
    setError(null);
    setBusy(true);
    try {
      // throws on unknown provider / misconfigured IdP — surfaced below
      await boundedSignIn(provider, {
        redirectTo: invite ? `/invite/${invite}` : "/app",
      });
    } catch (err) {
      setError(errorMessage(err, `${provider} sign-in is not available yet`));
      setBusy(false);
    }
  }

  const resetStep = mode === "reset" && resetSent;
  // A pending code is a mid-flow gate (ticket #197): the OAuth block and
  // the email divider belong to the pre-flow auth choice, so they stand
  // down for the duration of code entry — tapping them there would
  // abandon the pending code for a different account flow.
  const verifyStep = mode === "verify" && codeSent;

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="text-sm text-muted hover:underline">
          ← ToDoTogether
        </Link>
        {/* Brand mark (ticket #120): static from public/, cut from
            docs/design/brand/mark.svg. */}
        <img
          src="/mark.svg"
          alt="ToDoTogether"
          width={56}
          height={56}
          className="mt-5 mb-3 block"
        />
        <h1 className="mt-4 text-2xl font-bold tracking-tight text-ink">
          {mode === "signup"
            ? "Create your account"
            : mode === "reset"
              ? resetStep
                ? "Check your email"
                : "Reset your password"
              : mode === "verify"
                ? "Verify your email"
                : "Welcome back"}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {mode === "verify"
            ? codeSent
              ? // The address is the contract: which inbox the code is in.
                <>
                  Enter the code we sent to{" "}
                  <span data-testid="verify-email" className="font-medium text-ink">
                    {email}
                  </span>
                  .
                </>
              : "We'll email you a code to finish creating your account."
            : resetStep
              ? "Enter the code we sent, then choose a new password."
              : mode === "reset"
                ? "We'll email you a code to set a new password."
                : mode === "signup"
                  ? "Free: 10 lists, share with two people."
                  : "Sign in to your shared lists."}
        </p>
        {mode === "verify" && (
          <p className="mt-1 text-sm text-muted" data-testid="verify-gate-copy">
            You can create and join lists once your email is verified.
          </p>
        )}

        {!verifyStep && (
          <div className="mt-6 space-y-2">
            <button
              type="button"
              data-testid="google-signin"
              onClick={() => oauth("google")}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-line bg-page px-4 py-2.5 text-sm font-medium text-ink shadow-sm hover:bg-pressed disabled:opacity-50"
            >
              <GoogleMark />
              Continue with Google
            </button>
            <button
              type="button"
              data-testid="apple-signin"
              onClick={() => oauth("apple")}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-line bg-page px-4 py-2.5 text-sm font-medium text-ink shadow-sm hover:bg-pressed disabled:opacity-50"
            >
              <AppleMark />
              Continue with Apple
            </button>
            {import.meta.env.VITE_MOCK_AUTH === "1" && (
              <button
                type="button"
                data-testid="mock-signin"
                onClick={() => oauth("mock")}
                disabled={busy}
                className="w-full rounded-lg border border-dashed border-line bg-page px-4 py-2.5 text-sm font-medium text-ink shadow-sm hover:bg-pressed disabled:opacity-50"
              >
                Continue with Mock IdP (dev)
              </button>
            )}
          </div>
        )}

        {!verifyStep && (
          <div className="my-5 flex items-center gap-3 text-xs text-muted">
            <span className="h-px flex-1 bg-line" />
            or with email
            <span className="h-px flex-1 bg-line" />
          </div>
        )}

        <form
          onSubmit={submitPasswordFlow}
          className={verifyStep ? "mt-6 space-y-3" : "space-y-3"}
        >
          {/* Verify step 2 shows the address in the subtitle instead of an
              editable field — the code's mailbox is fixed. */}
          {!(mode === "verify" && codeSent) && (
            <input
              type="email"
              required
              data-testid="email-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-line bg-page px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-blue focus:outline-none"
            />
          )}
          {mode !== "reset" && mode !== "verify" && (
            <input
              type="password"
              required
              data-testid="password-input"
              // The min-8 hint is a sign-up constraint (ticket #197):
              // echoed on every sign-in it is noise, so sign-in gets the
              // bare word.
              placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-line bg-page px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-blue focus:outline-none"
            />
          )}
          {resetStep && (
            <>
              <input
                type="text"
                required
                data-testid="reset-code-input"
                placeholder="Code from the email"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full rounded-lg border border-line bg-page px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-blue focus:outline-none"
              />
              <input
                type="password"
                required
                data-testid="new-password-input"
                placeholder="New password (min 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-lg border border-line bg-page px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-blue focus:outline-none"
              />
            </>
          )}
          {mode === "verify" && codeSent && (
            <input
              type="text"
              required
              data-testid="verify-code-input"
              placeholder="Code from the email"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-lg border border-line bg-page px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-blue focus:outline-none"
            />
          )}
          {error && (
            <p
              data-testid="auth-error"
              className="rounded-lg border border-danger/40 bg-page px-3 py-2 text-sm text-danger"
              role="alert"
            >
              {error}
            </p>
          )}
          {mode === "verify" && codeSent && (
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                data-testid="verify-resend"
                onClick={resendCode}
                disabled={busy}
                className="underline text-muted hover:text-ink disabled:opacity-50"
              >
                Resend code
              </button>
              {resent && (
                <span data-testid="verify-resent" className="text-muted">
                  Code resent
                </span>
              )}
            </div>
          )}
          <button
            type="submit"
            data-testid={mode === "verify" ? "verify-submit" : "auth-submit"}
            disabled={busy}
            className="w-full rounded-lg bg-blue-fill px-4 py-2.5 text-sm font-semibold text-on-blue shadow-sm hover:bg-blue-fill-pressed disabled:opacity-50"
          >
            {busy
              ? "…"
              : mode === "signup"
                ? "Create account"
                : mode === "reset"
                  ? resetStep
                    ? "Set new password"
                    : "Email me a reset code"
                  : mode === "verify"
                    ? codeSent
                      ? "Verify your email"
                      : "Email me a code"
                    : "Sign in"}
          </button>
        </form>

        <div className="mt-5 space-y-1.5 text-sm text-muted">
          {mode !== "signin" && (
            <p>
              Already have an account?{" "}
              <Link
                to={`/signin${inviteQuery}`}
                className="font-medium underline"
                data-testid="to-signin"
              >
                Sign in
              </Link>
            </p>
          )}
          {mode !== "signup" && (
            <p>
              New here?{" "}
              <Link
                to={`/signup${inviteQuery}`}
                className="font-medium underline"
                data-testid="to-signup"
              >
                Create an account
              </Link>
            </p>
          )}
          {mode !== "reset" && mode !== "verify" && (
            <p>
              <Link
                to={`/reset${inviteQuery}`}
                className="underline"
                data-testid="to-reset"
              >
                Forgot your password?
              </Link>
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
